/**
 * ColumnVisibilityDropdown - Shared toggle dropdown for column visibility.
 * Used by collection and deck table views with different column key types.
 */
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';

import { useClickAway } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { FaCheck, FaColumns } from 'react-icons/fa';

// ============================================================================
// Types
// ============================================================================

export interface ColumnGroup<K extends string> {
  label: string;
  columns: K[];
}

export interface ColumnVisibilityDropdownProps<K extends string> {
  /** Current visibility state for each column key */
  visibility: Record<K, boolean>;
  /** Callback to toggle a single column's visibility */
  onToggle: (column: K) => void;
  /** Reset all columns to defaults */
  onReset: () => void;
  /** Column groups to display in the dropdown */
  groups: ColumnGroup<K>[];
  /** Display labels for each column key */
  labels: Record<K, string>;
  /** Columns that cannot be toggled (always visible) */
  alwaysVisible?: K[];
  /** Columns that display inline (shown as a hint tooltip) */
  inlineColumns?: K[];
  /** Number of active optional columns (shown as badge) */
  activeCount: number;
  /** Optional class name for the container */
  className?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export default function ColumnVisibilityDropdown<K extends string>({
  visibility,
  onToggle,
  onReset,
  groups,
  labels,
  alwaysVisible = [],
  inlineColumns = [],
  activeCount,
  className,
}: ColumnVisibilityDropdownProps<K>): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useClickAway<HTMLDivElement>(() => setIsOpen(false));

  const handleToggle = useCallback(
    (column: K) => {
      if (alwaysVisible.includes(column)) return;
      onToggle(column);
    },
    [alwaysVisible, onToggle],
  );

  return (
    <div ref={dropdownRef} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
          isOpen ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Toggle column visibility"
      >
        <FaColumns className="h-3.5 w-3.5" />
        <span>Columns</span>
        {activeCount > 0 && (
          <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-zinc-600 text-[10px] font-semibold text-zinc-200">
            {activeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute top-full right-0 z-50 mt-1 max-h-96 w-56 overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-800 py-1 shadow-xl"
          role="menu"
        >
          {/* Column groups */}
          {groups.map((group, groupIndex) => (
            <div key={group.label}>
              {groupIndex > 0 && <div className="my-1 border-t border-zinc-700" />}
              <div className="px-3 py-2 text-xs font-medium tracking-wider text-zinc-500 uppercase">{group.label}</div>

              {group.columns.map((column) => {
                const isActive = visibility[column];
                const isAlwaysVisible = alwaysVisible.includes(column);
                const isInline = inlineColumns.includes(column);

                return (
                  <button
                    key={column}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={isActive}
                    onClick={() => handleToggle(column)}
                    disabled={isAlwaysVisible}
                    className={clsx(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                      isAlwaysVisible ? 'cursor-not-allowed opacity-50' : 'hover:bg-zinc-700/50',
                      isActive ? 'text-zinc-100' : 'text-zinc-400',
                    )}
                    title={isAlwaysVisible ? 'This column is always visible' : isInline ? 'Displayed inline with card name' : undefined}
                  >
                    <span
                      className={clsx(
                        'flex h-4 w-4 items-center justify-center rounded border transition-colors',
                        isActive ? 'border-aether bg-aether text-zinc-900' : 'border-zinc-600 bg-transparent',
                      )}
                    >
                      {isActive && <FaCheck className="h-2.5 w-2.5" />}
                    </span>
                    <span>{labels[column]}</span>
                  </button>
                );
              })}
            </div>
          ))}

          {/* Reset option */}
          <div className="my-1 border-t border-zinc-700" />
          <button
            type="button"
            onClick={onReset}
            className="w-full px-3 py-2 text-left text-xs text-zinc-500 hover:bg-zinc-700/50 hover:text-zinc-300"
          >
            Reset to Defaults
          </button>
        </div>
      )}
    </div>
  );
}
